// Карточки быстрого старта и то, какие из них сегодня можно нажать. SMY-109
//
// Список лежит отдельно от оболочки, потому что читают его двое — сетка на
// главном и левая колонка, — и правило доступности у них одно. Разъехаться
// им нельзя: серая карточка на главном и живая строка в колонке рядом с ней
// выглядят не ограничением, а поломкой.

import type { ScenarioId } from '@smyslika/core'

import type { IconName } from './icons.ts'

export interface Task {
  slug: string
  scenario: ScenarioId
  icon: IconName
}

/**
 * Быстрый старт в новом чате. Названия и подсказки берутся из ядра, а не
 * повторяются здесь: SCENARIO_COPY уже содержит их, и вторая копия разошлась
 * бы с первой в день первой же правки текста.
 *
 * ⚠️ ЗДЕСЬ НЕТ НИ ПРОВЕРКИ ФАКТОВ, НИ ПОИСКА — обоих сценариев не стало
 * 06.09.2026, и по одной причине: их формат отвечал не на вопрос человека.
 * Проверка — утверждениями со статусами, поиск — пересказом выдачи.
 *
 * Плитки под поиск здесь тоже нет, и это не пропуск. Веб-поиск остался, но
 * выбирать его больше нечем и незачем: он стал частью обычного ответа, и
 * решает за него классификатор. Плитка «Найти» рядом с «Спросить» предлагала
 * бы выбрать между вопросом и вопросом.
 */
export const TASKS: Task[] = [
  { slug: 'write', scenario: 'write', icon: 'write' },
  { slug: 'doc-review', scenario: 'doc_review', icon: 'docReview' },
  { slug: 'doc-create', scenario: 'doc_create', icon: 'docCreate' },
  { slug: 'image', scenario: 'image', icon: 'image' },
]

/**
 * Как называется РЕЗУЛЬТАТ сценария — в истории, в журнале списаний и в
 * блоке недавних.
 *
 * Не то же, что `SCENARIO_COPY.card` из ядра: там имя действия («Спросить»),
 * а здесь имя того, что получилось («Ответ в чате»). Списком строка
 * отвечает на вопрос «что это», а не «что сделать», и подставить туда команду
 * значило бы предложить повторить задачу вместо того, чтобы её открыть.
 */
export const RESULT_TITLES: Record<string, string> = {
  chat: 'Ответ в чате',
  write: 'Написанный текст',
  doc_review: 'Разбор документа',
  doc_create: 'Созданный документ',
  image: 'Изображение',
}

/** Обратный поиск: по адресу узнать, какой сценарий за ним стоит. */
export const taskBySlug = (slug: string): Task | undefined => TASKS.find((t) => t.slug === slug)

/**
 * Почему карточка выключена.
 *
 * Продуктовыми словами и без названий поставщиков: правило тона запрещает
 * называть модели в навигации, а человеку и незачем знать, чей ключ кончился.
 * Названа при этом и цена вопроса — «ничего не спишется», — потому что первое,
 * что думает человек про серую кнопку, это «а деньги уже взяли?».
 */
export const UNAVAILABLE_HINT = 'Сейчас не запускается — попробуй позже. Ничего не спишется.'

/** То же одной строкой: подпись под названием и всплывающая подсказка. */
export const UNAVAILABLE_SHORT = 'Сейчас не запускается'

/**
 * Запустится ли сценарий на самом деле.
 *
 * Ответ приходит с сервера полем `available` в `/v1/reference`: там сходятся
 * оба шлюза — живой поставщик в конфигурации и написанный workflow в реестре.
 * Клиенту решать нечем, и выводить это из своей таблицы он не должен.
 *
 * Справочник без списка сценариев — не то же самое, что «всё выключено»:
 * поле приходит только с сервера, поднятого с базой, и молчание тут значит
 * «не спрашивали». Погасить по нему всю сетку значило бы объявить продукт
 * сломанным там, где он просто ещё не ответил.
 */
export function isTaskAvailable(
  /**
   * Справочник с сервера. Тип структурный, а не `Reference` из
   * `@smyslika/api-client`: этому пакету от справочника нужен ровно один
   * список, а зависимость на клиент API он завёл бы целиком — ради типа,
   * который здесь и так описан двумя полями.
   */
  reference: { scenarios?: readonly { id: ScenarioId; available?: boolean }[] } | null,
  scenario: ScenarioId,
): boolean {
  const list = reference?.scenarios
  if (list == null) return true
  return list.find((s) => s.id === scenario)?.available ?? true
}
